import { useEffect } from "react";
import { useLanguage, languages, type Language } from "@/lib/i18n";

interface SEOHeadProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'product';
  product?: {
    name: string;
    price: string;
    currency: string;
    availability: boolean;
    rating?: number;
    reviewCount?: number;
    image?: string;
    description?: string;
    highPrice?: string;
    sku?: string;
    localPrice?: number;
    localCurrency?: string;
    reviews?: { author: string; rating: number; body: string }[];
  };
  article?: {
    headline: string;
    description: string;
    datePublished: string;
    dateModified: string;
    image?: string;
    keywords?: string[];
    wordCount?: number;
  };
  breadcrumbs?: { name: string; url?: string }[];
  howToSteps?: { name: string; text: string }[];
  faqItems?: { question: string; answer: string }[];
  itemList?: { name: string; url: string; image?: string; price?: number; currency?: string; position: number }[];
  isHomePage?: boolean;
  dateModified?: string;
  speakableSelectors?: string[];
}

const BASE_URL = 'https://pdrn-skin.com';

/** Locale-aware default titles for key markets */
const localeTitles: Partial<Record<Language, string>> = {
  'en-GB': 'PDRN Skincare UK | Korean Salmon DNA Serum, Masks & Cream | Free UK Delivery',
  'en-AU': 'PDRN Skincare Australia | Korean Salmon DNA Serum, Masks & Cream | Free AU Shipping',
};

/** Locale-aware default descriptions for key markets */
const localeDescriptions: Partial<Record<Language, string>> = {
  'en-GB': 'Buy PDRN skincare in the UK. Shop Korean salmon DNA serum, sheet masks & capsule cream with free UK delivery. Clinically proven anti-ageing results for glass skin. Prices from £32.',
  'en-AU': 'Buy PDRN skincare in Australia. Shop Korean salmon DNA serum, sheet masks & capsule cream with free AU shipping. Clinically proven anti-ageing results for glass skin. Prices from A$59.',
};

/** Locale-aware meta keywords for geo-targeted SEO */
const localeKeywords: Partial<Record<Language, string>> = {
  'en-GB': 'buy pdrn uk, pdrn serum uk, pdrn skincare uk, korean skincare uk, salmon dna serum uk, best korean skincare uk, pdrn sheet mask uk, glass skin uk, k-beauty uk, anti-ageing serum uk, korean face masks uk, pdrn cream uk, best face serum uk, collagen cream uk, korean beauty products uk',
};

const localeMap: Record<Language, string> = {
  'en-US': 'en_US',
  'en-GB': 'en_GB',
  'en-AU': 'en_AU',
  he: 'he_IL',
  hr: 'hr_HR',
  fi: 'fi_FI',
  fr: 'fr_FR',
  de: 'de_DE',
  no: 'nb_NO',
  es: 'es_ES',
  ar: 'ar_AE',
};

const languageNameMap: Record<Language, string> = {
  'en-US': 'English',
  'en-GB': 'English',
  'en-AU': 'English',
  he: 'Hebrew',
  hr: 'Croatian',
  fi: 'Finnish',
  fr: 'French',
  de: 'German',
  no: 'Norwegian',
  es: 'Spanish',
  ar: 'Arabic',
};

function setOrCreateMeta(attr: string, attrValue: string, content: string) {
  let el = document.querySelector(`meta[${attr}="${attrValue}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setOrCreateLink(rel: string, href: string, extraAttrs?: Record<string, string>) {
  const selector = extraAttrs
    ? `link[rel="${rel}"]${Object.entries(extraAttrs).map(([k, v]) => `[${k}="${v}"]`).join('')}`
    : `link[rel="${rel}"]`;
  let el = document.querySelector(selector) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    if (extraAttrs) {
      Object.entries(extraAttrs).forEach(([k, v]) => el!.setAttribute(k, v));
    }
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function removeMetaBySelector(selector: string) {
  document.querySelectorAll(selector).forEach(el => el.remove());
}

/** Build canonical URL with ?lang= for non-default languages */
function buildCanonicalUrl(path: string, language: Language): string {
  if (language === 'en-US') return `${BASE_URL}${path}`;
  const separator = path.includes('?') ? '&' : '?';
  return `${BASE_URL}${path}${separator}lang=${language}`;
}

/** Build lang URL for hreflang */
function buildLangUrl(path: string, langCode: Language): string {
  if (langCode === 'en-US') return `${BASE_URL}${path}`;
  const separator = path.includes('?') ? '&' : '?';
  return `${BASE_URL}${path}${separator}lang=${langCode}`;
}

export function SEOHead({ 
  title, description, image, url, type = 'website', 
  product, article, breadcrumbs, howToSteps, faqItems, itemList, isHomePage,
  dateModified, speakableSelectors
}: SEOHeadProps) {
  const { language } = useLanguage();

  useEffect(() => {
    const defaultTitle = localeTitles[language] || 'PDRN Skincare | Korean Salmon DNA Serum, Masks & Cream | Glass Skin';
    const defaultDesc = localeDescriptions[language] || 'Shop PDRN skincare powered by Korean salmon DNA technology. Our PDRN serum, sheet masks & capsule cream deliver the glass skin effect. Clinically proven PDRN skincare products with free shipping.';
    const fullTitle = title || defaultTitle;
    const fullDesc = description || defaultDesc;
    const fullImage = image || `${BASE_URL}/og-image.png`;
    const currentPath = window.location.pathname;
    const canonicalUrl = url ? buildCanonicalUrl(new URL(url).pathname, language) : buildCanonicalUrl(currentPath, language);
    const currentLocale = localeMap[language] || 'en_US';

    // Title
    document.title = fullTitle;

    // Core meta
    setOrCreateMeta('name', 'description', fullDesc);
    setOrCreateMeta('name', 'robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    setOrCreateMeta('name', 'author', 'PDRN SKIN');

    // Locale-specific meta keywords (UK geo-targeting boost)
    const geoKeywords = localeKeywords[language];
    if (geoKeywords) {
      setOrCreateMeta('name', 'keywords', geoKeywords);
    }

    // Open Graph
    setOrCreateMeta('property', 'og:title', fullTitle);
    setOrCreateMeta('property', 'og:description', fullDesc);
    setOrCreateMeta('property', 'og:image', fullImage);
    setOrCreateMeta('property', 'og:url', canonicalUrl);
    setOrCreateMeta('property', 'og:type', article ? 'article' : type === 'product' ? 'product' : 'website');
    setOrCreateMeta('property', 'og:site_name', 'PDRN SKIN');
    setOrCreateMeta('property', 'og:locale', currentLocale);

    // OG locale alternates
    removeMetaBySelector('meta[property^="og:locale:alternate"]');
    languages.forEach((lang) => {
      if (lang.code !== language) {
        const altLocale = localeMap[lang.code];
        if (altLocale) {
          const meta = document.createElement('meta');
          meta.setAttribute('property', 'og:locale:alternate');
          meta.setAttribute('content', altLocale);
          document.head.appendChild(meta);
        }
      }
    });

    // Product-specific OG meta
    if (product) {
      const displayPrice = product.localPrice?.toString() || product.price;
      const displayCurrency = product.localCurrency || product.currency;
      setOrCreateMeta('property', 'product:price:amount', displayPrice);
      setOrCreateMeta('property', 'product:price:currency', displayCurrency);
      setOrCreateMeta('property', 'product:availability', product.availability ? 'in stock' : 'out of stock');
      if (product.rating) {
        setOrCreateMeta('property', 'product:rating:average', product.rating.toString());
        setOrCreateMeta('property', 'product:rating:count', (product.reviewCount || 127).toString());
      }
    }

    // Article tag meta for blog posts
    if (article?.keywords?.length) {
      removeMetaBySelector('meta[property="article:tag"]');
      article.keywords.forEach(keyword => {
        const meta = document.createElement('meta');
        meta.setAttribute('property', 'article:tag');
        meta.setAttribute('content', keyword);
        document.head.appendChild(meta);
      });
    }

    // Twitter Card
    setOrCreateMeta('name', 'twitter:card', 'summary_large_image');
    setOrCreateMeta('name', 'twitter:title', fullTitle);
    setOrCreateMeta('name', 'twitter:description', fullDesc);
    setOrCreateMeta('name', 'twitter:image', fullImage);
    setOrCreateMeta('name', 'twitter:site', '@PDRNSKIN');

    // Canonical — language-aware
    setOrCreateLink('canonical', canonicalUrl);

    // Hreflang
    const hreflangMap: Record<string, string> = {
      'en-US': 'en-US', 'en-GB': 'en-GB', 'en-AU': 'en-AU',
      'he': 'he', 'hr': 'hr', 'fi': 'fi', 'fr': 'fr',
      'de': 'de', 'no': 'nb', 'es': 'es', 'ar': 'ar',
    };
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
    languages.forEach((lang) => {
      const hreflangCode = hreflangMap[lang.code] || lang.code;
      const langUrl = buildLangUrl(currentPath, lang.code);
      setOrCreateLink('alternate', langUrl, { hreflang: hreflangCode });
    });
    setOrCreateLink('alternate', `${BASE_URL}${currentPath}`, { hreflang: 'x-default' });

    // === JSON-LD Structured Data (Graph approach) ===
    const existingLD = document.querySelector('script[data-seo-jsonld]');
    if (existingLD) existingLD.remove();

    const graph: Record<string, unknown>[] = [];

    // 1. WebPage with Speakable
    const webPage: Record<string, unknown> = {
      '@type': 'WebPage',
      '@id': `${canonicalUrl}#webpage`,
      url: canonicalUrl,
      name: fullTitle,
      description: fullDesc,
      inLanguage: currentLocale,
      isPartOf: { '@id': `${BASE_URL}#website` },
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: speakableSelectors || ['.hero-subtitle', '.product-description', '.faq-answer', '.science-citation'],
      },
    };
    if (dateModified) webPage.dateModified = dateModified;
    graph.push(webPage);

    // 2. Organization (homepage) — all 11 languages
    if (isHomePage) {
      const allLanguageNames = [...new Set(Object.values(languageNameMap))];
      const sameAsUrls = languages
        .filter(l => l.code !== 'en-US')
        .map(l => buildLangUrl('/', l.code));

      graph.push({
        '@type': 'Organization',
        '@id': `${BASE_URL}#organization`,
        name: 'PDRN SKIN',
        url: BASE_URL,
        logo: {
          '@type': 'ImageObject',
          url: `${BASE_URL}/og-image.png`,
          width: 1200,
          height: 630,
        },
        description: 'PDRN skincare brand specializing in Korean salmon DNA serum, sheet masks, and capsule cream for the glass skin effect.',
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer service',
          availableLanguage: allLanguageNames,
        },
        areaServed: [
          { '@type': 'Country', name: 'United Kingdom' },
          { '@type': 'Country', name: 'United States' },
          { '@type': 'Country', name: 'Australia' },
          { '@type': 'Country', name: 'Israel' },
          { '@type': 'Country', name: 'Germany' },
          { '@type': 'Country', name: 'France' },
          { '@type': 'Country', name: 'Spain' },
          { '@type': 'Country', name: 'Finland' },
          { '@type': 'Country', name: 'Croatia' },
          { '@type': 'Country', name: 'Norway' },
          { '@type': 'Country', name: 'United Arab Emirates' },
        ],
        sameAs: sameAsUrls,
      });

      // 3. WebSite — all 11 locales, NO SearchAction (no search on site)
      graph.push({
        '@type': 'WebSite',
        '@id': `${BASE_URL}#website`,
        url: BASE_URL,
        name: 'PDRN SKIN',
        description: 'PDRN Skincare — Korean Salmon DNA Serum, Masks & Cream',
        publisher: { '@id': `${BASE_URL}#organization` },
        inLanguage: languages.map(l => localeMap[l.code]),
      });
    }

    // 4. Product Schema — local currency pricing + Review schema
    if (product) {
      const displayPrice = product.localPrice?.toString() || product.price;
      const displayCurrency = product.localCurrency || product.currency;

      const productSchema: Record<string, unknown> = {
        '@type': 'Product',
        '@id': `${canonicalUrl}#product`,
        name: product.name,
        description: product.description || fullDesc,
        image: product.image || fullImage,
        brand: { '@type': 'Brand', name: 'PDRN SKIN' },
        manufacturer: { '@type': 'Organization', name: 'PDRN SKIN' },
        category: 'Skincare',
        material: 'Salmon DNA (PDRN)',
        ...(product.sku ? { sku: product.sku } : {}),
        offers: {
          '@type': 'Offer',
          price: displayPrice,
          priceCurrency: displayCurrency,
          priceValidUntil: '2026-12-31',
          availability: product.availability
            ? 'https://schema.org/InStock'
            : 'https://schema.org/OutOfStock',
          url: canonicalUrl,
          seller: { '@id': `${BASE_URL}#organization` },
          shippingDetails: {
            '@type': 'OfferShippingDetails',
            shippingRate: {
              '@type': 'MonetaryAmount',
              value: '0',
              currency: displayCurrency,
            },
            shippingDestination: {
              '@type': 'DefinedRegion',
              addressCountry: ['US', 'GB', 'AU', 'IL', 'DE', 'FR', 'ES', 'FI', 'HR', 'NO', 'AE'],
            },
            deliveryTime: {
              '@type': 'ShippingDeliveryTime',
              handlingTime: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 3, unitCode: 'DAY' },
              transitTime: { '@type': 'QuantitativeValue', minValue: 5, maxValue: 14, unitCode: 'DAY' },
            },
          },
          hasMerchantReturnPolicy: {
            '@type': 'MerchantReturnPolicy',
            applicableCountry: ['US', 'GB', 'AU', 'IL', 'DE', 'FR', 'ES', 'FI', 'HR', 'NO', 'AE'],
            returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
            merchantReturnDays: 30,
            returnMethod: 'https://schema.org/ReturnByMail',
            returnFees: 'https://schema.org/FreeReturn',
          },
        },
      };

      // Aggregate rating
      if (product.rating) {
        productSchema.aggregateRating = {
          '@type': 'AggregateRating',
          ratingValue: product.rating.toString(),
          reviewCount: (product.reviewCount || 127).toString(),
          bestRating: '5',
          worstRating: '1',
        };
      }

      // Individual Review schema (up to 3 reviews for rich snippets)
      if (product.reviews?.length) {
        productSchema.review = product.reviews.slice(0, 3).map(r => ({
          '@type': 'Review',
          author: { '@type': 'Person', name: r.author },
          reviewRating: { '@type': 'Rating', ratingValue: r.rating.toString(), bestRating: '5' },
          reviewBody: r.body,
        }));
      }

      // Topical entities — Knowledge Graph linking
      productSchema.about = [
        { '@type': 'Thing', name: 'Polydeoxyribonucleotide', sameAs: 'https://en.wikipedia.org/wiki/Polydeoxyribonucleotide' },
        { '@type': 'Thing', name: 'Salmon DNA', sameAs: 'https://en.wikipedia.org/wiki/Salmon' },
        { '@type': 'Thing', name: 'Korean Skincare', sameAs: 'https://en.wikipedia.org/wiki/Korean_beauty' },
      ];
      productSchema.mentions = [
        { '@type': 'Thing', name: 'Glass Skin', description: 'A Korean beauty trend characterized by translucent, dewy, poreless skin' },
        { '@type': 'Thing', name: 'Collagen', sameAs: 'https://en.wikipedia.org/wiki/Collagen' },
        { '@type': 'Thing', name: 'Hyaluronic Acid', sameAs: 'https://en.wikipedia.org/wiki/Hyaluronic_acid' },
      ];

      graph.push(productSchema);
    }

    // 5. BreadcrumbList
    if (breadcrumbs?.length) {
      graph.push({
        '@type': 'BreadcrumbList',
        '@id': `${canonicalUrl}#breadcrumb`,
        itemListElement: breadcrumbs.map((crumb, idx) => ({
          '@type': 'ListItem',
          position: idx + 1,
          name: crumb.name,
          ...(crumb.url ? { item: crumb.url } : {}),
        })),
      });
    }

    // 6. HowTo Schema
    if (howToSteps?.length) {
      graph.push({
        '@type': 'HowTo',
        name: `How to Use ${product?.name || 'PDRN Skincare'}`,
        description: `Step-by-step guide for using ${product?.name || 'PDRN skincare products'}`,
        totalTime: 'PT20M',
        step: howToSteps.map((step, idx) => ({
          '@type': 'HowToStep',
          position: idx + 1,
          name: step.name,
          text: step.text,
        })),
      });
    }

    // 7. FAQPage Schema
    if (faqItems?.length) {
      graph.push({
        '@type': 'FAQPage',
        '@id': `${canonicalUrl}#faq`,
        mainEntity: faqItems.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: { '@type': 'Answer', text: item.answer },
        })),
      });
    }

    // 8. ItemList Schema (homepage product carousel)
    if (itemList?.length) {
      graph.push({
        '@type': 'ItemList',
        '@id': `${canonicalUrl}#itemlist`,
        itemListElement: itemList.map((item) => ({
          '@type': 'ListItem',
          position: item.position,
          url: item.url,
          name: item.name,
          ...(item.image ? { image: item.image } : {}),
        })),
      });
    }

    // 9. Article Schema (blog posts) — enriched for AI/GEO
    if (article) {
      const articleImageUrl = article.image || fullImage;
      graph.push({
        '@type': 'Article',
        '@id': `${canonicalUrl}#article`,
        headline: article.headline,
        description: article.description,
        image: { '@type': 'ImageObject', url: articleImageUrl, width: 1200, height: 630 },
        datePublished: article.datePublished,
        dateModified: article.dateModified,
        wordCount: article.wordCount || 1200,
        isAccessibleForFree: true,
        author: { '@type': 'Organization', name: 'PDRN SKIN', url: BASE_URL, sameAs: [BASE_URL] },
        publisher: {
          '@type': 'Organization',
          name: 'PDRN SKIN',
          url: BASE_URL,
          logo: { '@type': 'ImageObject', url: `${BASE_URL}/og-image.png`, width: 1200, height: 630 },
        },
        mainEntityOfPage: { '@id': `${canonicalUrl}#webpage` },
        inLanguage: currentLocale,
        speakable: {
          '@type': 'SpeakableSpecification',
          cssSelector: ['h1', 'h2', '.article-excerpt', '.article-section-title'],
        },
        about: [
          { '@type': 'Thing', name: 'Polydeoxyribonucleotide', sameAs: 'https://en.wikipedia.org/wiki/Polydeoxyribonucleotide' },
          { '@type': 'Thing', name: 'Salmon DNA Skincare', sameAs: 'https://en.wikipedia.org/wiki/Salmon' },
          { '@type': 'Thing', name: 'Korean Beauty', sameAs: 'https://en.wikipedia.org/wiki/Korean_beauty' },
        ],
        citation: [
          {
            '@type': 'CreativeWork',
            name: 'Polydeoxyribonucleotide promotes skin regeneration by stimulating fibroblast proliferation',
            url: 'https://pubmed.ncbi.nlm.nih.gov/',
          },
          {
            '@type': 'CreativeWork',
            name: 'PDRN activates the A2A adenosine receptor pathway, enhancing tissue repair',
            url: 'https://www.mdpi.com/journal/ijms',
          },
        ],
        ...(article.keywords?.length ? { keywords: article.keywords.join(', ') } : {}),
      });
    }

    // Write JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo-jsonld', 'true');
    script.textContent = JSON.stringify({ '@context': 'https://schema.org', '@graph': graph });
    document.head.appendChild(script);

    return () => {
      const ldScript = document.querySelector('script[data-seo-jsonld]');
      if (ldScript) ldScript.remove();
      removeMetaBySelector('meta[property^="og:locale:alternate"]');
      removeMetaBySelector('meta[property="article:tag"]');
    };
  }, [title, description, image, url, type, product, article, language, breadcrumbs, howToSteps, faqItems, itemList, isHomePage, dateModified, speakableSelectors]);

  return null;
}
